import React, { useCallback, useEffect, useState, useMemo, useContext, useRef } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import backend from "../../backend";
import AuthContext from "../../AuthContext";
import useTitle from "../../hooks/useTitle";
import usePulidoLink from "../../hooks/usePulidoLink";
import ExpandableTable from "../../components/design-system/ExpandableTable";
import ConfirmModal from "../../components/design-system/ConfirmModal";
import StatusBadge from "../../components/design-system/StatusBadge";
import PieceLookup from "../../components/design-system/PieceLookup";
import DocumentAttacher from "../../components/design-system/DocumentAttacher";
import CierreFacturacionModal from "../../components/design-system/CierreFacturacionModal";
import OcSinRegistrarCard from "../../components/design-system/OcSinRegistrarCard";

// ─── constantes ──────────────────────────────────────────────────────────────

// F5.9 — Solo 2 estados de CO: en_proceso (default al crear) y facturada
// (cuando todas las WOs espejo están facturado). El resto fue eliminado.
const STATUS_OPTIONS = [
  { value: "en_proceso", label: "En proceso" },
  { value: "facturada",  label: "Facturada"  },
];

const S = {
  wrap: {
    // clamp: 22px lateral en desktop, ~10px en móvil (360-430px)
    display: "block", padding: "clamp(12px, 2.5vw, 18px) clamp(10px, 3vw, 22px)", background: "#1a1a1a",
    minHeight: "100%", color: "#ddd", fontFamily: "Arial,sans-serif",
    fontSize: 13, textAlign: "left", boxSizing: "border-box", width: "100%",
  },
  card: {
    background: "#242424", border: "1px solid #2e2e2e",
    borderRadius: 8, padding: "14px 16px", marginBottom: 14,
  },
  label: {
    fontSize: 10, fontWeight: 700, color: "#999",
    textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 4,
  },
  input: {
    background: "#161616", border: "1px solid #333", borderRadius: 5,
    padding: "7px 10px", fontSize: 12, color: "#ddd",
    fontFamily: "Arial,sans-serif", width: "100%", boxSizing: "border-box",
  },
  sectionTitle: {
    fontSize: 10, fontWeight: 700, color: "#999",
    textTransform: "uppercase", letterSpacing: ".07em",
    margin: "14px 0 8px",
  },
};

// ─── Borrador del form "Registrar nueva OC" (28-ago) ─────────────────────────
// Patrón de QuotationForm: persistencia en sessionStorage para que el estado
// sobreviva la navegación (ej. ir al catálogo y volver). Botón "Guardar
// borrador" lo escribe; al montar se restaura; "Descartar borrador" lo limpia.
const CO_DRAFT_KEY = "binimeliz:customerOrderForm:draft:v1";
const CO_DRAFT_MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24 h — localStorage: sobrevive cerrar el navegador (01-sep)

const loadCODraft = () => {
  try {
    const raw = localStorage.getItem(CO_DRAFT_KEY);
    if (!raw) return null;
    const d = JSON.parse(raw);
    if (!d?.savedAt || Date.now() - d.savedAt > CO_DRAFT_MAX_AGE_MS) {
      localStorage.removeItem(CO_DRAFT_KEY);
      return null;
    }
    return d;
  } catch { return null; }
};
const clearCODraft = () => { try { localStorage.removeItem(CO_DRAFT_KEY); } catch {} };

// Normaliza ítems al formato { sku, desc, qty, pUnit, source, pieceId }.
// Prioridad: catálogo (it.piece) > WO espejo (workOrderPieces[idx].piece) > campo crudo.
const normItems = (items = [], workOrderPieces = []) =>
  items.map((it, idx) => {
    const wp    = workOrderPieces[idx];
    const piece = it.piece || wp?.piece;   // piece obj del catálogo (populado)
    return {
      sku:     piece?.sku   || it.sku     || "",
      desc:    piece?.name  || it.name    || it.desc    || "",
      qty:     it.quantity  || it.qty     || wp?.quantity || 1,
      pUnit:   piece?.price || wp?.price  || it.unitPrice || it.pUnit || 0,
      source:  piece ? "catalog" : null,
      pieceId: piece?._id   || null,
    };
  });

// ─── componente principal ─────────────────────────────────────────────────────

const CustomerOrders = () => {
  useTitle("OC Clientes");
  const navigate  = useNavigate();
  const location  = useLocation();

  // 28-ago (auditoría roles) — registrar/editar/facturar/cancelar/eliminar OCs
  // es de admin/operator. El seller entra en modo consulta (el backend igual
  // rechaza; esto evita ofrecer botones que terminan en 403).
  const auth = useContext(AuthContext);
  const userRoles = auth?.user?.roles || [];
  const puedeOperar = ["admin", "operator"].some(r => userRoles.includes(r));
  const esAdmin     = userRoles.includes("admin");

  // 28-ago — PRECARGA desde /oc-entrantes (correo recepcion@):
  // ?oc=NUMERO&cliente=NOMBRE&mailId=ID → abre el form de registrar con el
  // N° OC y el cliente prellenados; al crear la OC con ese mailId presente,
  // el correo se marca "registrada" solo (cierre automático del loop del vigía).
  const [mailPrefill] = useState(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      const oc      = p.get("oc") || "";
      const cliente = p.get("cliente") || "";
      const mailId  = p.get("mailId") || "";
      return (oc || cliente || mailId) ? { oc, cliente, mailId } : null;
    } catch { return null; }
  });
  const [mailIdPend, setMailIdPend] = useState(mailPrefill?.mailId || null);
  const clientePrefillDone = useRef(false);

  const PER_PAGE = 50;
  const [page, setPage] = useState(1);
  const [customerOrders, setCustomerOrders] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [clients, setClients]   = useState([]);

  // form nueva OC
  const [fClient, setFClient] = useState("");
  const [fOCNum,  setFOCNum]  = useState("");
  const [fDate,   setFDate]   = useState("");
  const [newItems, setNewItems] = useState([]);
  const [saving,  setSaving]  = useState(false);
  const [lastReg, setLastReg] = useState(null);
  const [ocCheck, setOcCheck] = useState(null);

  // 28-ago — form colapsable (cerrado por defecto si hay OCs listadas) + borrador
  const [formOpen, setFormOpen] = useState(false);
  const [hasDraft, setHasDraft] = useState(false);
  const [draftMsg, setDraftMsg] = useState(null);

  // delete modal
  const [deleteTarget, setDeleteTarget] = useState(null);

  // cancelar OC modal — cascade a WOs activas
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelling,   setCancelling]   = useState(false);

  // marcar facturada modal (también usado para editar facturación existente)
  const [facturarTarget, setFacturarTarget] = useState(null);

  // filtros (tarjetas + buscador)
  const [activeFilter, setActiveFilter] = useState("all");
  const [searchQuery,  setSearchQuery]  = useState("");

  // F5.8 — extracción IA de PDF de OC
  const [pdfUploading,    setPdfUploading]    = useState(false);
  const [pdfError,        setPdfError]        = useState("");
  const [pdfExtracted,    setPdfExtracted]    = useState(null); // { fileName }
  const [attachmentDocId, setAttachmentDocId] = useState(null);
  const fileInputRef = React.useRef(null);

  const resetPdfAttachment = () => {
    setPdfExtracted(null);
    setAttachmentDocId(null);
    setPdfError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handlePdfUpload = async file => {
    if (!file) return;
    if (file.type !== "application/pdf") {
      setPdfError("El archivo debe ser PDF");
      return;
    }
    setPdfError("");
    setPdfUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await backend.post("customerOrder/extract-pdf", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const { documentId, extracted, aiError } = res.data;
      setAttachmentDocId(documentId);

      if (aiError || !extracted) {
        setPdfError(aiError || "La IA no pudo extraer datos");
        setPdfExtracted({ fileName: file.name, partial: true });
        return;
      }

      // Pre-llenar form con datos extraídos
      if (extracted.clientOCNumber) {
        setFOCNum(extracted.clientOCNumber);
        clearTimeout(window._ocCheckTimer);
        window._ocCheckTimer = setTimeout(
          () => verificarOC(extracted.clientOCNumber, ""),
          300
        );
      }
      if (extracted.date) setFDate(extracted.date);

      // Match cliente por nombre (substring case-insensitive)
      if (extracted.clientName && clients.length > 0) {
        const target = extracted.clientName.toLowerCase().trim();
        const match = clients.find(c => {
          const n = (c.name || "").toLowerCase();
          return n.includes(target) || target.includes(n);
        });
        if (match) setFClient(match._id);
      }

      // Pre-llenar items + auto-cruce con catálogo (SKU exacto OR descripción exacta).
      // Si match único → source: "catalog" + pieceId. Si no → source: "extracted"
      // (texto extraído pendiente de revisión: el usuario debe asociar o registrar nuevo).
      let matchedCount   = 0;
      let extractedCount = 0;
      if (Array.isArray(extracted.items) && extracted.items.length > 0) {
        const resolveItem = async (it) => {
          // Código normalizado por el backend cuando la OC lo trae en la
          // descripción o sin espacios (06-oct, OC 35709).
          const sku  = (it.codigoDetectado || it.sku || "").trim();
          const desc = (it.desc || "").trim();
          // Lo que decía la OC, para mostrarlo bajo la línea y poder comparar.
          const deLaOC = {
            ocTexto:      [(it.sku || "").trim(), desc].filter(t => t && t !== "-").join(" · "),
            ocNombre:     it.nombreOC || "",
            ocCandidatas: it.candidatas || [],
          };
          // El backend ya cruzó contra el catálogo (columna de código o descripción).
          if (it.match) {
            return {
              sku:     it.match.sku  || "",
              desc:    it.match.name || "",
              qty:     Number(it.qty)   || 1,
              pUnit:   Number(it.pUnit) || it.match.price || 0,
              source:  "catalog",
              pieceId: it.match._id,
              ...deLaOC,
            };
          }
          let match = null;
          // Calce dudoso (dos piezas posibles): no se asocia sola, se revisa a mano.
          if (deLaOC.ocCandidatas.length === 0) try {
            // Cruce por SKU exacto (case-insensitive). Backend ya soporta sku|name|skuAliases.
            if (sku.length >= 2) {
              const r = await backend.get(`piece/search?q=${encodeURIComponent(sku)}`);
              const skuLower = sku.toLowerCase();
              match = (r.data || []).find(p =>
                (p.sku || "").toLowerCase() === skuLower ||
                ((p.skuAliases || []).map(a => (a || "").toLowerCase())).includes(skuLower)
              ) || null;
            }
            // Si no match por SKU, intentar por descripción exacta (case-insensitive).
            if (!match && desc.length >= 2) {
              const r = await backend.get(`piece/search?q=${encodeURIComponent(desc)}`);
              const dLower = desc.toLowerCase();
              match = (r.data || []).find(p => (p.name || "").toLowerCase() === dLower) || null;
            }
          } catch { /* network / 4xx — caer a "extracted" */ }
          if (match) {
            return {
              sku:     match.sku  || "",
              desc:    match.name || "",
              qty:     Number(it.qty)   || 1,
              pUnit:   Number(it.pUnit) || match.price || 0,
              source:  "catalog",
              pieceId: match._id,
              ...deLaOC,
            };
          }
          return {
            sku, desc,
            qty:     Number(it.qty)   || 1,
            pUnit:   Number(it.pUnit) || 0,
            source:  "extracted",      // pendiente revisión: asociar o registrar nuevo
            pieceId: null,
            ...deLaOC,
          };
        };
        const resolved = await Promise.all(extracted.items.map(resolveItem));
        matchedCount   = resolved.filter(r => r.source === "catalog").length;
        extractedCount = resolved.length - matchedCount;
        setNewItems(resolved);
      }

      setPdfExtracted({
        fileName:       file.name,
        clientName:     extracted.clientName,
        itemCount:      extracted.items?.length || 0,
        matchedCount,
        extractedCount,
        clientMatched:  clients.some(c => {
          const t = (extracted.clientName || "").toLowerCase().trim();
          const n = (c.name || "").toLowerCase();
          return t && (n.includes(t) || t.includes(n));
        }),
      });
    } catch (e) {
      setPdfError(e?.response?.data?.error || e.message || "Error subiendo PDF");
      resetPdfAttachment();
    }
    setPdfUploading(false);
  };

  // ── data ──────────────────────────────────────────────────────────────────

  const verificarOC = async (ocNum, clientId) => {
    if (!ocNum || ocNum.length < 3) { setOcCheck(null); return; }
    setOcCheck("checking");
    try {
      const res = await backend.get(
        `workOrder/verificar-oc?oc=${encodeURIComponent(ocNum)}&client=${clientId || ""}`
      );
      setOcCheck(res.data.existe
        ? { tipo: "duplicado", wo: res.data.workOrder }
        : { tipo: "ok" }
      );
    } catch { setOcCheck(null); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [coRes, clRes] = await Promise.all([
        backend.get("customerOrder"),
        backend.get("entity").catch(() => ({ data: [] })),
      ]);
      setCustomerOrders(
        (coRes.data || []).map(co => ({
          ...co,
          items: normItems(co.items, co.workOrders?.[0]?.pieces),
        }))
      );
      setClients(clRes.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // 28-ago — restaurar borrador al montar (si existe y no venció) y abrir el form.
  useEffect(() => {
    const d = loadCODraft();
    if (!d) return;
    if (d.client) setFClient(d.client);
    if (d.ocNum) {
      setFOCNum(d.ocNum);
      clearTimeout(window._ocCheckTimer);
      window._ocCheckTimer = setTimeout(() => verificarOC(d.ocNum, d.client || ""), 400);
    }
    if (d.date) setFDate(d.date);
    if (Array.isArray(d.items) && d.items.length > 0) setNewItems(d.items);
    setHasDraft(true);
    setFormOpen(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 28-ago — si no hay OCs listadas tras cargar, el form parte abierto.
  useEffect(() => {
    if (!loading && customerOrders.length === 0) setFormOpen(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  // 28-ago — precarga desde el correo (?oc=&cliente=&mailId=): abrir el form
  // con el N° OC prellenado. Solo para quien puede registrar (admin/operator).
  useEffect(() => {
    if (!mailPrefill || !puedeOperar) return;
    setFormOpen(true);
    if (mailPrefill.oc) {
      setFOCNum(mailPrefill.oc);
      clearTimeout(window._ocCheckTimer);
      window._ocCheckTimer = setTimeout(() => verificarOC(mailPrefill.oc, ""), 400);
    }
    // Limpiar la query de la URL para que un refresh no re-dispare la precarga.
    navigate("/customerOrders", { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 28-ago — el cliente llega como NOMBRE (clienteDetectado del vigía) y el
  // form usa un select por _id: matchear cuando la lista de clientes cargue
  // (substring case-insensitive, mismo criterio del flujo PDF-IA). Una sola vez.
  useEffect(() => {
    if (!mailPrefill?.cliente || !puedeOperar) return;
    if (clientePrefillDone.current || clients.length === 0) return;
    clientePrefillDone.current = true;
    const target = mailPrefill.cliente.toLowerCase().trim();
    const match = clients.find(c => {
      const n = (c.name || "").toLowerCase();
      return n.includes(target) || target.includes(n);
    });
    if (match) setFClient(prev => prev || match._id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clients]);

  const guardarBorrador = () => {
    try {
      localStorage.setItem(CO_DRAFT_KEY, JSON.stringify({
        client: fClient, ocNum: fOCNum, date: fDate, items: newItems,
        savedAt: Date.now(),
      }));
      setHasDraft(true);
      setDraftMsg("✓ Borrador guardado — se restaura al volver a esta pantalla.");
      clearTimeout(window._coDraftMsgTimer);
      window._coDraftMsgTimer = setTimeout(() => setDraftMsg(null), 4000);
    } catch {
      alert("No se pudo guardar el borrador en este navegador.");
    }
  };

  const descartarBorrador = () => {
    clearCODraft();
    setHasDraft(false);
    setDraftMsg(null);
    setFClient(""); setFOCNum(""); setFDate(""); setNewItems([]); setOcCheck(null);
    resetPdfAttachment();
  };

  useEffect(() => {
    if (location.state?.new) {
      const co = location.state.new;
      setCustomerOrders(p => [
        { ...co, items: normItems(co.items, co.workOrders?.[0]?.pieces) },
        ...p,
      ]);
      navigate("", { replace: true });
    }
    if (location.state?.edit) {
      const co = location.state.edit;
      setCustomerOrders(p => p.map(v => v._id === co._id
        ? { ...co, items: normItems(co.items, co.workOrders?.[0]?.pieces) } : v
      ));
      navigate("", { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  // Métricas para cards clickables (reemplazan los chips antiguos)
  const isDriveImport = c => /Importada del Drive/i.test(c.observations || "");
  const total      = customerOrders.length;
  const sinOC      = customerOrders.filter(c => !c.clientOCNumber).length;
  const enProceso  = customerOrders.filter(c => ["en_proceso", "registered", "partial"].includes(c.status)).length;
  const facturadas = customerOrders.filter(c => ["facturada", "complete"].includes(c.status)).length;
  const sinItems   = customerOrders.filter(c => !c.items || c.items.length === 0).length;
  const drive      = customerOrders.filter(isDriveImport).length;

  // ── filtros (cards clickables + búsqueda) ───────────────────────────────
  // F5.9 — orden por N° OT desc (la columna primaria del listado).
  const filteredOrders = useMemo(() => customerOrders.filter(co => {
    if (activeFilter === "sinOC"      && co.clientOCNumber) return false;
    if (activeFilter === "enProceso"  && !["en_proceso", "registered", "partial"].includes(co.status)) return false;
    if (activeFilter === "facturadas" && !["facturada", "complete"].includes(co.status)) return false;
    if (activeFilter === "sinItems"   && (co.items?.length || 0) > 0) return false;
    if (activeFilter === "drive"      && !isDriveImport(co)) return false;

    const q = searchQuery.trim().toLowerCase();
    if (!q) return true;
    const woNum = String(co.workOrders?.[0]?.number || co.number || "");
    return (
      (co.clientOCNumber || "").toLowerCase().includes(q) ||
      woNum.toLowerCase().includes(q) ||
      (co.client?.name || "").toLowerCase().includes(q) ||
      (co.items || []).some(it =>
        (it.sku  || "").toLowerCase().includes(q) ||
        (it.desc || "").toLowerCase().includes(q)
      )
    );
  }).sort((a, b) => {
    const na = a.workOrders?.[0]?.number || a.number || 0;
    const nb = b.workOrders?.[0]?.number || b.number || 0;
    return nb - na;
  }), [customerOrders, activeFilter, searchQuery]);

  const isFiltered = activeFilter !== "all" || !!searchQuery.trim();

  // Paginación 50 por página (igual a /trabajos)
  const totalPages = Math.ceil(filteredOrders.length / PER_PAGE) || 1;
  const pageRows = useMemo(() => {
    const start = (page - 1) * PER_PAGE;
    return filteredOrders.slice(start, start + PER_PAGE);
  }, [filteredOrders, page]);
  useEffect(() => { setPage(1); }, [activeFilter, searchQuery]);

  // ── helpers form nueva OC ─────────────────────────────────────────────────
  const getProductosLabel = co => {
    const first = co.items?.[0];
    if (!first) return "sin ítems cargados";
    const resto = co.items.length > 1 ? ` +${co.items.length - 1}` : "";
    return first.sku
      ? `${first.sku}${resto}`
      : `${first.desc || "ítem sin descripción"}${resto}`;
  };

  const handleSubmit = async () => {
    if (!fClient) { alert("Selecciona un cliente."); return; }
    if (ocCheck?.tipo === "duplicado") {
      alert(`Esta OC ya está registrada como N° ${ocCheck.wo?.number}.`);
      return;
    }
    setSaving(true);
    try {
      const res = await backend.post("customerOrder", {
        client:         fClient,
        clientOCNumber: fOCNum || undefined,
        date:           fDate  || undefined,
        items: newItems.map(it => ({
          // Schema CO.items acepta: piece, quantity, sku, desc, qty, pUnit, source.
          // Mandar ambos quantity+qty y pUnit por compat backward.
          piece:    it.pieceId || undefined,
          sku:      it.sku  || undefined,
          desc:     it.desc || undefined,
          quantity: it.qty  || 1,
          qty:      it.qty  || 1,
          pUnit:    it.pUnit || 0,
          // "extracted" es estado UX frontend; en DB se persiste como "temp"
          // (item ad-hoc no linkeado a catálogo).
          source:   it.source === "extracted" ? "temp" : (it.source || undefined),
        })),
        attachmentDocId: attachmentDocId || undefined,
      });
      const data = res.data;
      setCustomerOrders(p => [
        { ...data, items: normItems(data.items) },
        ...p,
      ]);
      setLastReg({ number: data.number || data.workOrder?.number, clientOCNumber: fOCNum });
      setFClient(""); setFOCNum(""); setFDate(""); setNewItems([]); setOcCheck(null);
      resetPdfAttachment();
      // 28-ago — la OC quedó registrada: el borrador ya no aplica.
      clearCODraft();
      setHasDraft(false);
      setDraftMsg(null);
      // 28-ago — cierre automático del loop del vigía: si venimos del correo
      // (?mailId=), marcar ese correo como "registrada" apuntando a esta OC.
      // No bloqueante: la OC ya quedó creada aunque este PATCH falle.
      if (mailIdPend) {
        try {
          await backend.patch(`mailEntrante/${mailIdPend}/estado`, {
            estado: "registrada",
            registradaEn: data._id,
          });
        } catch (err) {
          console.error("[CustomerOrders] No se pudo marcar el correo como registrado:", err);
        }
        setMailIdPend(null);
      }
    } catch (e) {
      alert("Error al registrar OC: " + (e?.response?.data?.error || e.message));
    }
    setSaving(false);
  };

  // ── ExpandableTable handlers ───────────────────────────────────────────────

  const handleSave = async (id, draft) => {
    await backend.patch(`customerOrder/${id}`, {
      client:         draft.client?._id || draft.client,
      clientOCNumber: draft.clientOCNumber || undefined,
      date:           draft.date   || undefined,
      status:         draft.status,
      stockStatus:    draft.stockStatus,
      observations:   draft.observations || undefined,
      items: (draft.items || []).map(it => ({
        piece:    it.pieceId || undefined,
        quantity: it.qty || 1,
        sku:      it.sku,
        desc:     it.desc,
        qty:      it.qty || 1,
        pUnit:    it.pUnit || 0,
        source:   it.source,
      })),
    });
    setCustomerOrders(prev => prev.map(co =>
      co._id === id ? { ...co, ...draft } : co
    ));
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await backend.delete(`customerOrder/${deleteTarget._id}`);
      setCustomerOrders(prev => prev.filter(co => co._id !== deleteTarget._id));
      setDeleteTarget(null);
    } catch (e) {
      alert("Error al eliminar: " + (e?.response?.data?.error || e.message));
    }
  };

  const handleCancel = async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    try {
      await backend.patch(`customerOrder/${cancelTarget._id}`, { status: "cancelled" });
      // Reflejo local: marcar la CO como cancelada + cualquier WO espejo activa.
      setCustomerOrders(prev => prev.map(co =>
        co._id === cancelTarget._id
          ? {
              ...co,
              status: "cancelled",
              closedAt: co.closedAt || new Date().toISOString(),
              workOrders: (co.workOrders || []).map(wo =>
                ["delivered", "facturado", "cancelled"].includes(wo.status)
                  ? wo
                  : { ...wo, status: "cancelled" }
              ),
            }
          : co
      ));
      setCancelTarget(null);
    } catch (e) {
      alert("Error al cancelar: " + (e?.response?.data?.error || e.message));
    }
    setCancelling(false);
  };

  // D-T51b — Link directo desde /pulido (?openId=<coId>&editField=<campo>)
  const pulidoLink = usePulidoLink({
    rows: customerOrders,
    getNumber: r => r.number,
  });

  // ── ExpandableTable config ────────────────────────────────────────────────
  // F5.4.2 — listado con paridad de info de WO (Fecha, Total, status dual,
  // link clickeable a la OT). Decisión Maxim 30-abr.

  const fmtDate = d => d
    ? new Date(d).toLocaleDateString("es-CL", { day: "2-digit", month: "short", year: "2-digit" })
    : "fecha no registrada";
  const fmtTotal = n => Number(n || 0).toLocaleString("es-CL");

  const goToWO = (e, woId) => {
    if (!woId) return;
    e.stopPropagation();
    e.preventDefault();
    navigate(`/workOrders?openId=${woId}`);
  };

  // F5.9 — N° OT como primera columna en TODAS las tablas. Stock se quita
  // (ese cruce se hace en /workOrders, no acá). Estado simplificado a solo CO.
  const columns = [
    {
      key: "woNumber", label: "N° OT", width: 90,
      stopPropagation: true,
      render: (_, row) => {
        const wo = row.workOrders?.[0];
        const num = wo?.number || row.number;
        if (!num) return <span style={{ color: "#999" }}>OT no generada</span>;
        return (
          <a
            href={`/workOrders?openId=${wo?._id || ""}`}
            onClick={e => goToWO(e, wo?._id)}
            style={{
              fontFamily: "monospace", color: "#3498db", fontWeight: 700,
              textDecoration: "none", cursor: wo?._id ? "pointer" : "default",
            }}
            title={wo?._id ? `Ir a OT N° ${num}` : `OT N° ${num}`}
          >
            {num}
          </a>
        );
      },
    },
    {
      key: "date", label: "Fecha", width: 85,
      render: (_, row) => (
        <span style={{ color: "#cbd5e1", fontSize: 11 }}>
          {fmtDate(row.date || row.createdAt)}
        </span>
      ),
    },
    {
      key: "client", label: "Cliente", width: 200,
      render: (_, row) => {
        const name = row.client?.name || "cliente no registrado";
        return (
          <span title={name} style={{
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            display: "inline-block", maxWidth: "100%",
          }}>
            {name}
          </span>
        );
      },
    },
    {
      key: "clientOCNumber", label: "OC cliente", width: 110,
      render: (_, row) => (
        <span style={{
          fontFamily: "monospace",
          color: "#3498db",
          fontWeight: 600,
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          display: "inline-block", maxWidth: "100%",
        }}>
          {row.clientOCNumber || "sin N° OC (verbal)"}
        </span>
      ),
    },
    {
      key: "productos", label: "Descripción",
      render: (_, row) => {
        const label = getProductosLabel(row);
        return (
          <span title={label} style={{
            fontFamily: "monospace", fontSize: 11,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            display: "inline-block", maxWidth: "100%",
          }}>
            {label}
          </span>
        );
      },
    },
    {
      key: "total", label: "Total", width: 110, align: "right",
      render: (_, row) => {
        const t = row.workOrders?.[0]?.total || 0;
        return (
          <span style={{
            color: t > 0 ? "#2ecc71" : "#888",
            fontWeight: t > 0 ? 600 : 400,
          }}>
            {t > 0 ? `$${fmtTotal(t)}` : "sin precio cargado"}
          </span>
        );
      },
    },
    {
      key: "status", label: "Estado", width: 130,
      render: (_, row) => (
        <span>
          <StatusBadge status={row.status || "en_proceso"} />
          {isDriveImport(row) && (
            <span title="Importada del Drive ENTREGAS"
              style={{
                marginLeft: 6, fontSize: 9, color: "#3498db",
                background: "#0d1e2e", border: "1px solid #3498db44",
                padding: "1px 5px", borderRadius: 3, fontWeight: 700,
              }}
            >drive</span>
          )}
        </span>
      ),
    },
    // 28-ago (roles): la columna Facturar solo existe para admin/operator
    // (operator SÍ emite factura/guía; el seller queda en consulta).
    ...(puedeOperar ? [{
      key: "actions", label: "Acción", width: 130, align: "right",
      stopPropagation: true,
      render: (_, row) => {
        const isInvoiced = !!row.invoicedAt;
        return (
          <button
            onClick={e => { e.stopPropagation(); setFacturarTarget(row); }}
            style={{
              background: isInvoiced ? "#3498db22" : "#2ecc7122",
              color: isInvoiced ? "#3498db" : "#2ecc71",
              border: `1px solid ${isInvoiced ? "#3498db55" : "#2ecc7155"}`,
              borderRadius: 4, padding: "3px 8px",
              fontSize: 10, cursor: "pointer", fontWeight: 600,
            }}
            title={isInvoiced ? "Editar fecha/N° de facturación" : "Marcar OC como facturada"}
          >
            {isInvoiced ? "✎ Editar fact." : "✓ Facturar"}
          </button>
        );
      },
    }] : []),
  ];

  const renderDetail = (data, helpers) => {
    const fmt = n => Number(n || 0).toLocaleString("es-CL");
    const subtotal = (data.items || []).reduce(
      (s, it) => s + (it.pUnit || 0) * (it.qty || 1), 0
    );
    const woNum = data.number || data.workOrders?.[0]?.number;

    const wo = data.workOrders?.[0];
    const woId = wo?._id;

    return (
      <div className="bini-expand">
        {/* header del detalle */}
        <div className="bini-expand-header">
          <div>
            <div className="bini-expand-title">
              OC {data.clientOCNumber || `N°${data.number}`}
              {wo?.number && (
                <>
                  <span style={{ color: "#999", margin: "0 8px" }}>→</span>
                  <a
                    href={`/workOrders?openId=${woId}`}
                    onClick={e => goToWO(e, woId)}
                    style={{
                      color: "#3498db", textDecoration: "none",
                      fontFamily: "monospace", cursor: woId ? "pointer" : "default",
                    }}
                    title={woId ? "Ir a la OT espejo" : ""}
                  >
                    OT N° {wo.number}
                  </a>
                </>
              )}
            </div>
            <div className="bini-expand-sub">
              {data.client?.name || "cliente no registrado"} ·{" "}
              {data.date ? new Date(data.date).toLocaleDateString("es-CL") : "sin fecha"}
            </div>
          </div>
          <div className="bini-expand-actions">
            {/* 28-ago (roles): editar/cancelar/eliminar solo admin/operator —
                el seller ve el detalle en modo consulta. */}
            {!helpers.isEditing && puedeOperar && (
              <>
                <button
                  className="bini-btn bini-btn-edit"
                  onClick={e => { e.stopPropagation(); helpers.startEdit(); }}
                >
                  ✎ Editar
                </button>
                {/* F-audit-09-may: cancelado ya → no mostrar nada.
                    Facturada → mostrar disabled con tooltip explicativo (el
                    backend rechaza con 409; mostrarlo disabled comunica al
                    user POR QUÉ no puede en lugar de ocultar la acción).
                    Otros estados → habilitado. */}
                {data.status === "facturada" ? (
                  <button
                    disabled
                    style={{
                      background: "transparent",
                      color: "#7f8c8d",
                      border: "1px solid #7f8c8d55",
                      borderRadius: 4, padding: "5px 10px",
                      fontSize: 11, cursor: "not-allowed", fontWeight: 600,
                      opacity: 0.6,
                    }}
                    title="No se puede cancelar una OC ya facturada. Para revertir, primero hay que emitir nota de crédito y revertir el DTE; entonces un admin puede forzar."
                  >
                    ⊘ Cancelar OC
                  </button>
                ) : data.status !== "cancelled" && (
                  <button
                    onClick={e => { e.stopPropagation(); setCancelTarget(data); }}
                    style={{
                      background: "transparent",
                      color: "#f39c12",
                      border: "1px solid #f39c1255",
                      borderRadius: 4, padding: "5px 10px",
                      fontSize: 11, cursor: "pointer", fontWeight: 600,
                    }}
                    title="Cancelar la OC y todas sus OTs activas"
                  >
                    ⊘ Cancelar OC
                  </button>
                )}
                <button
                  className="bini-btn-icon danger"
                  onClick={e => { e.stopPropagation(); setDeleteTarget(data); }}
                >
                  Eliminar
                </button>
              </>
            )}
          </div>
        </div>

        {/* F5.4.2 — Información de la OT espejo */}
        {wo ? (
          <div style={{
            display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap",
            background: "var(--bini-bg-panel)",
            border: "1px solid var(--bini-border)",
            borderRadius: 4,
            padding: "8px 12px",
            margin: "10px 0",
            fontSize: 11,
          }}>
            <span style={{ color: "#cbd5e1", textTransform: "uppercase", letterSpacing: 0.5, fontSize: 10 }}>
              OT espejo
            </span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
              <span style={{ color: "#aaa" }}>Estado:</span>
              <StatusBadge status={wo.status || "pending"} />
            </span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
              <span style={{ color: "#aaa" }}>Total:</span>
              <span style={{ color: wo.total > 0 ? "#2ecc71" : "#888", fontWeight: 600 }}>
                {wo.total > 0 ? `$${fmt(wo.total)}` : "sin precio cargado"}
              </span>
            </span>
            {wo.origin && (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                <span style={{ color: "#aaa" }}>Origen:</span>
                <span style={{ color: "#aaa", fontFamily: "monospace", fontSize: 10 }}>
                  {wo.origin}
                </span>
              </span>
            )}
          </div>
        ) : (
          <div style={{
            border: "1px solid #4a3500", background: "#2d2000",
            borderRadius: 4, padding: "8px 12px",
            margin: "10px 0", fontSize: 11, color: "#f39c12",
          }}>
            ⚠ OT espejo no disponible — la WO pudo haberse eliminado.
          </div>
        )}

        {/* campos: 3 columnas (className="grid" colapsa a 2/1 en móvil) */}
        <div className="grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
          <div className="bini-field">
            <label>N° OC cliente</label>
            {helpers.isEditing ? (
              <input
                className="bini-input bini-mono"
                name="clientOCNumber"
                value={data.clientOCNumber || ""}
                onChange={e => helpers.updateDraft(d => ({ ...d, clientOCNumber: e.target.value }))}
              />
            ) : (
              <div className="val bini-mono">{data.clientOCNumber || "sin N° OC (verbal)"}</div>
            )}
          </div>
          <div className="bini-field">
            <label>Fecha OC</label>
            {helpers.isEditing ? (
              <input
                className="bini-input"
                name="date"
                type="date"
                value={data.date ? String(data.date).substring(0, 10) : ""}
                onChange={e => helpers.updateDraft(d => ({ ...d, date: e.target.value }))}
              />
            ) : (
              <div className="val">
                {data.date ? new Date(data.date).toLocaleDateString("es-CL") : "fecha de OC no registrada"}
              </div>
            )}
          </div>
          <div className="bini-field">
            <label>N° taller (OT)</label>
            <div className="val bini-mono" style={{ color: "#3498db" }}>
              {woNum || "OT no generada"}
            </div>
          </div>

          <div className="bini-field">
            <label>Cliente</label>
            <div className="val">{data.client?.name || "cliente no registrado"}</div>
          </div>
          <div className="bini-field">
            <label>Estado OC</label>
            {helpers.isEditing ? (
              <select
                className="bini-input"
                name="status"
                value={data.status || "en_proceso"}
                onChange={e => helpers.updateDraft(d => ({ ...d, status: e.target.value }))}
              >
                {STATUS_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            ) : (
              <div className="val">
                <StatusBadge status={data.status || "en_proceso"} />
              </div>
            )}
          </div>
          <div className="bini-field">
            <label>Fecha facturación</label>
            {helpers.isEditing ? (
              <input
                className="bini-input"
                name="invoicedAt"
                type="date"
                value={data.invoicedAt ? String(data.invoicedAt).substring(0, 10) : ""}
                onChange={e => {
                  const v = e.target.value;
                  helpers.updateDraft(d => ({
                    ...d,
                    invoicedAt: v || null,
                    // si se setea invoicedAt, también poblar closedAt si no existe
                    closedAt: v && !d.closedAt ? v : d.closedAt,
                    // si se limpia, también limpiar status facturada
                    ...(!v && d.status === "facturada" ? { status: "en_proceso" } : {}),
                  }));
                }}
              />
            ) : (
              <div className="val">
                {data.invoicedAt
                  ? new Date(data.invoicedAt).toLocaleDateString("es-CL")
                  : <span style={{ color: "#aaa" }}>pendiente de facturación</span>}
              </div>
            )}
          </div>
          <div className="bini-field">
            <label>Fecha cierre</label>
            {helpers.isEditing ? (
              <input
                className="bini-input"
                name="closedAt"
                type="date"
                value={data.closedAt ? String(data.closedAt).substring(0, 10) : ""}
                onChange={e => helpers.updateDraft(d => ({ ...d, closedAt: e.target.value || null }))}
              />
            ) : (
              <div className="val">
                {data.closedAt
                  ? new Date(data.closedAt).toLocaleDateString("es-CL")
                  : <span style={{ color: "#aaa" }}>{data.status === "cancelled" ? "cancelada" : "OC aún abierta"}</span>}
              </div>
            )}
          </div>
        </div>

        {/* ítems */}
        <div style={{
          display: "flex", justifyContent: "space-between",
          alignItems: "center", margin: "14px 0 6px",
        }}>
          <span className="bini-section-title" style={{ margin: 0 }}>
            Ítems ({data.items?.length || 0})
          </span>
          {helpers.isEditing && (
            <button
              className="bini-btn-add"
              onClick={e => {
                e.stopPropagation();
                helpers.updateDraft(d => ({
                  ...d,
                  items: [...(d.items || []), {
                    sku: "", desc: "", qty: 1, pUnit: 0, source: null, pieceId: null,
                  }],
                }));
              }}
            >
              + Agregar ítem
            </button>
          )}
        </div>

        <table className="bini-items-table">
          <colgroup>{[140, null, 50, 90, 100].map((w, i) => (
            <col key={i} style={w ? { width: w } : undefined} />
          ))}{helpers.isEditing && <col style={{ width: 30 }} />}</colgroup>
          <thead>
            <tr>
              <th>SKU</th>
              <th>Descripción</th>
              <th style={{ textAlign: "right" }}>Cant</th>
              <th style={{ textAlign: "right" }}>P. unit</th>
              <th style={{ textAlign: "right" }}>Subtotal</th>
              {helpers.isEditing && <th />}
            </tr>
          </thead>
          <tbody>
            {(data.items || []).length === 0 && (
              <tr>
                <td colSpan={helpers.isEditing ? 6 : 5}
                  style={{ textAlign: "center", color: "var(--bini-text-muted)", padding: 16 }}>
                  Sin ítems
                </td>
              </tr>
            )}
            {(data.items || []).map((it, idx) => {
              const sub = (it.pUnit || 0) * (it.qty || 1);
              if (helpers.isEditing) {
                return (
                  <tr key={idx} data-item-idx={idx}>
                    <td colSpan={2} style={{ padding: "4px 6px" }}>
                      <PieceLookup
                        name="sku"
                        value={it.sku || it.desc ? {
                          pieceId:     it.pieceId || null,
                          sku:         it.sku     || "",
                          description: it.desc    || "",
                          price:       it.pUnit   || 0,
                          source:      it.source  || null,
                          temporary:   !!it.temporary,
                          expiresAt:    it.expiresAt || null,
                        } : null}
                        onChange={updated => helpers.updateDraft(d => {
                          const items = [...(d.items || [])];
                          items[idx] = {
                            ...items[idx],
                            sku:     updated?.sku         || "",
                            desc:    updated?.description || "",
                            pUnit:   updated?.price       ?? items[idx].pUnit,
                            source:  updated?.source      || null,
                            pieceId: updated?.pieceId     || null,
                            temporary: !!updated?.temporary,
                            expiresAt: updated?.expiresAt || null,
                          };
                          return { ...d, items };
                        })}
                      />
                    </td>
                    <td style={{ padding: "4px 4px" }}>
                      <input
                        type="number" min={1}
                        name="qty"
                        value={it.qty || 1}
                        onFocus={e => e.target.select()}
                        onChange={e => helpers.updateDraft(d => {
                          const items = [...(d.items || [])];
                          items[idx] = { ...items[idx], qty: Number(e.target.value) };
                          return { ...d, items };
                        })}
                        style={{ width: "100%", textAlign: "right" }}
                      />
                    </td>
                    <td style={{ padding: "4px 4px" }}>
                      <input
                        type="number" min={0}
                        name="pUnit"
                        value={it.pUnit || 0}
                        onFocus={e => e.target.select()}
                        onChange={e => helpers.updateDraft(d => {
                          const items = [...(d.items || [])];
                          items[idx] = { ...items[idx], pUnit: Number(e.target.value) };
                          return { ...d, items };
                        })}
                        style={{ width: "100%", textAlign: "right" }}
                      />
                    </td>
                    <td className={`price ${!sub ? "zero" : ""}`}>
                      {sub ? `$${fmt(sub)}` : "sin precio"}
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <button
                        className="bini-btn-icon danger"
                        style={{ padding: "2px 5px", fontSize: 11 }}
                        onClick={() => helpers.updateDraft(d => ({
                          ...d, items: (d.items || []).filter((_, i) => i !== idx),
                        }))}
                      >✕</button>
                    </td>
                  </tr>
                );
              }
              return (
                <tr key={idx}>
                  <td>
                    <span style={{ fontFamily: "monospace", color: "#3498db" }}>
                      {it.sku || "sin SKU"}
                    </span>
                  </td>
                  <td className="desc">{it.desc || "sin descripción"}</td>
                  <td className="num">{it.qty || 1}</td>
                  <td className={`price ${!it.pUnit ? "zero" : ""}`}>
                    {it.pUnit ? `$${fmt(it.pUnit)}` : (
                      <span
                        title={
                          it.pieceId
                            ? "Click para cargar precio en catálogo"
                            : "Item sin SKU. Crear pieza en catálogo o ver detalle en /pulido"
                        }
                        style={{
                          color: "#cbd5e1",
                          fontStyle: "italic",
                          cursor: "pointer",
                          borderBottom: "1px dashed #888",
                        }}
                        onClick={e => {
                          e.stopPropagation();
                          if (it.sku) {
                            navigate(`/pieces?search=${encodeURIComponent(it.sku)}`);
                          } else if (it.pieceId) {
                            navigate(`/pieces?search=${it.pieceId}`);
                          } else if (esAdmin) {
                            navigate("/pulido#items-pendientes");
                          } else {
                            // /pulido es solo-admin: al resto lo mandamos al catálogo
                            navigate("/pieces?search=");
                          }
                        }}
                      >
                        sin precio
                      </span>
                    )}
                    {it.pUnit > 0 && (
                      <div
                        title="Snapshot del catálogo al momento de ingresar la OC. No se actualiza si cambia el catálogo después."
                        style={{
                          fontSize: 9,
                          color: "var(--bini-text-muted, #666)",
                          fontStyle: "italic",
                          marginTop: 2,
                          fontWeight: 400,
                        }}
                      >
                        válido al {fmtDate(data.date || data.createdAt)}
                      </div>
                    )}
                  </td>
                  <td className={`price ${!sub ? "zero" : ""}`}>
                    {sub ? `$${fmt(sub)}` : "sin precio"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <div className="bini-totals">
          <div className="bini-totals-box">
            <div className="bini-totals-row total">
              <span>Total ítems</span>
              <span className="v">${fmt(subtotal)}</span>
            </div>
          </div>
        </div>

        {/* F5.9 — PDF de OC adjunto (no bloqueante). Sirve tanto para ver
            el PDF que llegó del cliente como para subirlo si no estaba.
            Se filtra por docType=ocCliente para que solo aparezcan los
            relevantes en este módulo. */}
        {!helpers.isEditing && (
          <div style={{ marginTop: 14 }}>
            <div style={{
              fontSize: 10, fontWeight: 700, color: "#cbd5e1",
              textTransform: "uppercase", letterSpacing: ".07em",
              marginBottom: 6,
            }}>
              📄 Documento OC del cliente
            </div>
            <DocumentAttacher
              refField="customerOrderRefs"
              refId={data._id}
              defaultType="ocCliente"
            />
          </div>
        )}

        {/* F5.4.2 — Acción secundaria: ir a la OT espejo */}
        {!helpers.isEditing && woId && (
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
            <a
              href={`/workOrders?openId=${woId}`}
              onClick={e => goToWO(e, woId)}
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                color: "#3498db", textDecoration: "none",
                border: "1px solid #3498db44",
                background: "transparent",
                borderRadius: 4,
                padding: "5px 12px",
                fontSize: 11,
                cursor: "pointer",
              }}
              title={`Ir a OT N° ${wo?.number}`}
            >
              → Ver OT N° {wo?.number} en /workOrders
            </a>
          </div>
        )}

        {helpers.isEditing && (
          <>
            {woNum && (
              <div className="bini-warning">
                <div className="t">⚠ Cambios afectarán OT N°{woNum}</div>
                <div className="d">
                  Los ítems se sincronizan al WorkOrder espejo al guardar.
                </div>
              </div>
            )}
            <div className="bini-action-bar">
              <div className="bini-action-bar-l" />
              <div className="bini-action-bar-r">
                <button
                  className="bini-btn bini-btn-cancel"
                  onClick={e => { e.stopPropagation(); helpers.cancelEdit(); }}
                >
                  Cancelar
                </button>
                <button
                  className="bini-btn bini-btn-save"
                  disabled={helpers.isSaving}
                  onClick={e => { e.stopPropagation(); helpers.saveEdit(); }}
                >
                  {helpers.isSaving ? "Guardando…" : "Guardar cambios"}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  // ── render ────────────────────────────────────────────────────────────────

  return (
    <div style={S.wrap}>
      {/* PEDIDO MAXI — alerta de OC entrantes sin registrar (mismo origen que /oc-entrantes) */}
      <OcSinRegistrarCard />

      {/* header */}
      <div style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: "#fff" }}>OC de clientes</div>
          {!puedeOperar && (
            <span
              title="Su rol permite consultar las OC registradas; registrar, facturar o modificar OCs corresponde a administración/operaciones."
              style={{
                fontSize: 10, fontWeight: 700, color: "#8a8a8a",
                border: "1px solid #333", background: "#1e1e1e",
                borderRadius: 5, padding: "2px 8px",
                textTransform: "uppercase", letterSpacing: ".05em",
              }}
            >
              Solo consulta
            </span>
          )}
        </div>
      </div>

      {/* nota info */}
      <div style={{
        background: "#001a2a", borderLeft: "3px solid #3498db",
        padding: "8px 12px", fontSize: 11, color: "#7ec8e3",
        borderRadius: "0 5px 5px 0", marginBottom: 14, lineHeight: 1.5,
      }}>
        Registra las OC que llegan de clientes. El sistema asigna un número de taller
        y queda en el registro general como trabajo aprobado esperando fabricación.
      </div>

      {/* toast éxito */}
      {lastReg && (
        <div style={{
          background: "#0d2a15", border: "1px solid #2ecc71", borderRadius: 6,
          padding: "10px 14px", marginBottom: 14, fontSize: 12, color: "#2ecc71",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <span>
            ✅ OC registrada correctamente.
            {lastReg.clientOCNumber && <> N° OC cliente: <strong>{lastReg.clientOCNumber}</strong>.</>}
            {lastReg.number && <> Número de taller: <strong>N° {lastReg.number}</strong>.</>}
          </span>
          <button onClick={() => setLastReg(null)} style={{
            background: "transparent", border: "none", color: "#2ecc71", fontSize: 16, cursor: "pointer",
          }}>×</button>
        </div>
      )}

      {/* F5.9 — Tarjetas removidas. El filtro "Sin N° OC" se movió a un chip
          toggle al lado del buscador (más abajo en el listado). */}

      {/* formulario nueva OC — colapsable (28-ago): cerrado por defecto cuando
          hay OCs listadas; "+ Registrar OC" lo abre.
          Roles: registrar OCs es de admin/operator — al seller no se le ofrece. */}
      {puedeOperar && (
      <>
      <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "14px 0 8px", flexWrap: "wrap" }}>
        <span style={{ ...S.sectionTitle, margin: 0 }}>Registrar nueva OC de cliente</span>
        <button
          onClick={() => setFormOpen(o => !o)}
          style={{
            background: formOpen ? "#1e1e1e" : "#e30613",
            color: formOpen ? "#999" : "#fff",
            border: formOpen ? "1px solid #333" : "none",
            borderRadius: 6, padding: "5px 14px", fontSize: 11,
            fontWeight: 700, cursor: "pointer",
          }}
        >
          {formOpen ? "− Ocultar formulario" : "+ Registrar OC"}
        </button>
        {hasDraft && !formOpen && (
          <span style={{
            fontSize: 10, fontWeight: 700, color: "#f39c12",
            background: "#f39c1222", border: "1px solid #f39c12",
            borderRadius: 6, padding: "3px 10px",
          }}>
            borrador guardado
          </span>
        )}
      </div>
      {formOpen && (
      <div style={S.card}>

        {/* F5.8 — botón Adjuntar OC (PDF) + extracción IA */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          gap: 10, marginBottom: 12, flexWrap: "wrap",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf"
              style={{ display: "none" }}
              onChange={e => handlePdfUpload(e.target.files?.[0])}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={pdfUploading}
              style={{
                background: "transparent", border: "1px dashed #3498db",
                color: "#3498db", borderRadius: 5, padding: "7px 14px",
                fontSize: 12, cursor: pdfUploading ? "wait" : "pointer",
                display: "inline-flex", alignItems: "center", gap: 6,
              }}
              title="Sube el PDF de la OC y la IA pre-llenará el formulario"
            >
              {pdfUploading ? "⏳ Procesando PDF…" : "📎 Adjuntar OC (PDF)"}
            </button>
            {pdfExtracted && !pdfUploading && (
              <button
                type="button"
                onClick={resetPdfAttachment}
                style={{
                  background: "transparent", border: "1px solid #444",
                  color: "#cbd5e1", borderRadius: 5, padding: "5px 10px",
                  fontSize: 11, cursor: "pointer",
                }}
                title="Quitar PDF adjunto"
              >
                Quitar adjunto
              </button>
            )}
          </div>
          {pdfExtracted && (
            <span style={{ fontSize: 11, color: "#cbd5e1" }}>
              📄 {pdfExtracted.fileName}
            </span>
          )}
        </div>

        {pdfError && (
          <div style={{
            background: "#2d0000", border: "1px solid #4a0000",
            borderRadius: 5, padding: "8px 12px", marginBottom: 12,
            fontSize: 11, color: "#e74c3c",
          }}>
            ⚠ {pdfError}
          </div>
        )}

        {pdfExtracted && !pdfExtracted.partial && (
          <div style={{
            background: "#2d2000", border: "1px solid #f39c12",
            borderRadius: 5, padding: "10px 14px", marginBottom: 14,
            fontSize: 12, color: "#f39c12", lineHeight: 1.5,
          }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>
              🤖 Detectado por IA — verifica antes de registrar
            </div>
            <div style={{ fontSize: 11, color: "#d4a017" }}>
              Pre-llené el formulario con {pdfExtracted.itemCount} ítem{pdfExtracted.itemCount === 1 ? "" : "s"}
              {pdfExtracted.clientName && (
                <> de <strong>{pdfExtracted.clientName}</strong>
                  {!pdfExtracted.clientMatched && (
                    <span style={{ color: "#e74c3c" }}> · no encontré ese cliente en el catálogo, selecciónalo manualmente</span>
                  )}
                </>
              )}.
              Revisa cantidades, precios y descripciones — la IA puede equivocarse.
            </div>
            {(pdfExtracted.matchedCount > 0 || pdfExtracted.extractedCount > 0) && (
              <div style={{ fontSize: 11, marginTop: 6, color: "#d4a017" }}>
                <span style={{ color: "#2ecc71" }}>✓ {pdfExtracted.matchedCount} coincide{pdfExtracted.matchedCount === 1 ? "" : "n"} con catálogo</span>
                {pdfExtracted.extractedCount > 0 && (
                  <>
                    <span style={{ color: "#cbd5e1", margin: "0 8px" }}>·</span>
                    <span style={{ color: "#e67e22" }}>
                      ⚠ {pdfExtracted.extractedCount} requiere{pdfExtracted.extractedCount === 1 ? "" : "n"} revisión: asociá a SKU existente o registrá pieza nueva
                    </span>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "row", gap: 10, marginBottom: 12, flexWrap: "wrap" /* móvil */ }}>
          <div style={{ flex: 1, minWidth: 160 }}>
            <div style={S.label}>Cliente</div>
            <select value={fClient} onChange={e => setFClient(e.target.value)} style={S.input}>
              <option value="">Seleccionar cliente</option>
              {clients.map(c => (
                <option key={c._id} value={c._id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div style={{ flex: 1, minWidth: 150 }}>
            <div style={S.label}>N° OC del cliente</div>
            <input
              value={fOCNum}
              onChange={e => {
                const val = e.target.value;
                setFOCNum(val);
                clearTimeout(window._ocCheckTimer);
                window._ocCheckTimer = setTimeout(() => verificarOC(val, fClient), 500);
              }}
              placeholder="Ej: OC-45242"
              style={{
                ...S.input,
                borderColor: ocCheck?.tipo === "duplicado" ? "#e74c3c"
                           : ocCheck?.tipo === "ok"        ? "#2ecc71"
                           : undefined,
              }}
            />
            {ocCheck === "checking" && (
              <div style={{ marginTop: 4, fontSize: 11, color: "#cbd5e1" }}>⏳ Verificando...</div>
            )}
            {ocCheck?.tipo === "ok" && (
              <div style={{ marginTop: 4, fontSize: 11, padding: "4px 8px", borderRadius: 4, background: "#0d2a15", color: "#2ecc71", border: "1px solid #0d4020" }}>
                ✓ OC disponible
              </div>
            )}
            {ocCheck?.tipo === "duplicado" && (
              <div style={{ marginTop: 4, fontSize: 11, padding: "4px 8px", borderRadius: 4, background: "#2d0000", color: "#e74c3c", border: "1px solid #4a0000" }}>
                ⚠ OC ya registrada → N°{ocCheck.wo?.number}
              </div>
            )}
          </div>
          <div style={{ flex: 1, minWidth: 140 }}>
            <div style={S.label}>Fecha OC</div>
            <input
              type="date" value={fDate} onChange={e => setFDate(e.target.value)}
              style={S.input}
            />
          </div>
        </div>

        <div style={S.sectionTitle}>Productos solicitados</div>

        {newItems.length > 0 && (
          <table className="bini-items-table" style={{ marginBottom: 10 }}>
            <colgroup>{[140, null, 50, 90, 100, 30].map((w, i) => (
              <col key={i} style={w ? { width: w } : undefined} />
            ))}</colgroup>
            <thead>
              <tr>
                <th>SKU</th>
                <th>Descripción</th>
                <th style={{ textAlign: "right" }}>Cant</th>
                <th style={{ textAlign: "right" }}>P. unit</th>
                <th style={{ textAlign: "right" }}>Subtotal</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {newItems.map((it, idx) => {
                const sub = (it.pUnit || 0) * (it.qty || 1);
                return (
                  <tr key={idx}>
                    <td colSpan={2} style={{ padding: "4px 6px" }}>
                      <PieceLookup
                        value={it.sku || it.desc ? {
                          pieceId:     it.pieceId || null,
                          sku:         it.sku     || "",
                          description: it.desc    || "",
                          price:       it.pUnit   || 0,
                          source:      it.source  || null,
                          temporary:   !!it.temporary,
                          expiresAt:    it.expiresAt || null,
                        } : null}
                        onChange={updated => {
                          const items = [...newItems];
                          items[idx] = {
                            ...items[idx],
                            sku:     updated?.sku         || "",
                            desc:    updated?.description || "",
                            pUnit:   updated?.price       ?? items[idx].pUnit,
                            source:  updated?.source      || null,
                            pieceId: updated?.pieceId     || null,
                            temporary: !!updated?.temporary,
                            expiresAt: updated?.expiresAt || null,
                          };
                          setNewItems(items);
                        }}
                      />
                      {it.ocTexto && (() => {
                        // Lo que decía la OC del cliente, para comparar con la pieza asociada.
                        const nombreDistinto = !!it.pieceId && !!it.ocNombre &&
                          it.ocNombre.trim().toLowerCase() !== (it.desc || "").trim().toLowerCase();
                        const dudoso = !it.pieceId && (it.ocCandidatas || []).length > 0;
                        return (
                          <div style={{ fontSize: 11, marginTop: 3, color: nombreDistinto || dudoso ? "var(--bini-amber)" : "var(--bini-text-muted)" }}>
                            En la OC: {it.ocTexto}
                            {nombreDistinto && <> · el cliente la llama «{it.ocNombre}»</>}
                            {dudoso && <> · calce dudoso, revisar: {it.ocCandidatas.map(c => `${c.sku} ${c.name}`).join(" / ")}</>}
                          </div>
                        );
                      })()}
                    </td>
                    <td style={{ padding: "4px 4px" }}>
                      <input
                        type="number" min={1}
                        value={it.qty || 1}
                        onChange={e => {
                          const items = [...newItems];
                          items[idx] = { ...items[idx], qty: Number(e.target.value) };
                          setNewItems(items);
                        }}
                        style={{ width: "100%", textAlign: "right" }}
                      />
                    </td>
                    <td style={{ padding: "4px 4px" }}>
                      <input
                        type="number" min={0}
                        value={it.pUnit || 0}
                        onChange={e => {
                          const items = [...newItems];
                          items[idx] = { ...items[idx], pUnit: Number(e.target.value) };
                          setNewItems(items);
                        }}
                        style={{ width: "100%", textAlign: "right" }}
                      />
                    </td>
                    <td className={`price ${!sub ? "zero" : ""}`}>
                      {sub ? `$${sub.toLocaleString("es-CL")}` : "sin precio"}
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <button
                        className="bini-btn-icon danger"
                        style={{ padding: "2px 5px", fontSize: 11 }}
                        onClick={() => setNewItems(newItems.filter((_, i) => i !== idx))}
                      >✕</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        <button
          className="bini-btn-add"
          style={{ marginBottom: 12 }}
          onClick={() => setNewItems([
            ...newItems,
            { sku: "", desc: "", qty: 1, pUnit: 0, source: null, pieceId: null },
          ])}
        >+ Agregar ítem</button>

        <div style={{ display: "flex", flexDirection: "row", gap: 8, justifyContent: "flex-end", alignItems: "center", flexWrap: "wrap" }}>
          {draftMsg && (
            <span style={{ fontSize: 11, color: "#2ecc71", marginRight: "auto" }}>{draftMsg}</span>
          )}
          {hasDraft && (
            <button
              onClick={descartarBorrador}
              style={{
                background: "transparent", color: "#e74c3c", border: "1px solid #e74c3c55",
                borderRadius: 6, padding: "7px 14px", fontSize: 12, cursor: "pointer",
              }}
              title="Eliminar el borrador guardado y limpiar el formulario"
            >Descartar borrador</button>
          )}
          <button
            onClick={guardarBorrador}
            style={{
              background: "#1e1e1e", color: "#cbd5e1", border: "1px solid #333",
              borderRadius: 6, padding: "7px 14px", fontSize: 12, cursor: "pointer",
            }}
            title="Guardar el estado del formulario para retomarlo después (se restaura al volver)"
          >Guardar borrador</button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            style={{
              background: "#e30613", color: "#fff", border: "none",
              borderRadius: 6, padding: "7px 16px", fontSize: 12,
              fontWeight: 700, cursor: "pointer", opacity: saving ? 0.7 : 1,
            }}
          >{saving ? "Registrando…" : "Registrar OC"}</button>
        </div>
      </div>
      )}
      </>
      )}

      {/* Cards clickables (toggle filter exclusivo) */}
      <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        <COCard label="Total"            value={total}      color="#3498db"
          onClick={() => setActiveFilter("all")} active={activeFilter === "all"} />
        <COCard label="En proceso"       value={enProceso}  color="#f39c12"
          onClick={() => setActiveFilter(p => p === "enProceso" ? "all" : "enProceso")}
          active={activeFilter === "enProceso"}
          title="OCs activas, en producción o esperando facturación" />
        <COCard label="Facturadas"       value={facturadas} color="#2ecc71"
          onClick={() => setActiveFilter(p => p === "facturadas" ? "all" : "facturadas")}
          active={activeFilter === "facturadas"}
          title="OCs con todas las WOs ya facturadas" />
        <COCard label="Sin N° OC"        value={sinOC}      color="#a78bfa"
          onClick={() => setActiveFilter(p => p === "sinOC" ? "all" : "sinOC")}
          active={activeFilter === "sinOC"}
          title="Verbales — sin N° formal del cliente" />
        <COCard label="Sin items"        value={sinItems}   color="#e74c3c"
          onClick={() => setActiveFilter(p => p === "sinItems" ? "all" : "sinItems")}
          active={activeFilter === "sinItems"}
          title="OCs sin ítems cargados — datos del Drive pendientes" />
        <COCard label="Importadas Drive" value={drive}      color="#3498db"
          onClick={() => setActiveFilter(p => p === "drive" ? "all" : "drive")}
          active={activeFilter === "drive"}
          title="OCs creadas por el script de integración del Drive ENTREGAS" />
      </div>

      {/* tabla con expand inline */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        gap: 10, margin: "14px 0 8px", flexWrap: "wrap",
      }}>
        <span style={{
          fontSize: 10, fontWeight: 700, color: "#999",
          textTransform: "uppercase", letterSpacing: ".07em",
        }}>
          OC de clientes registradas
          {isFiltered && (
            <span style={{ color: "#cbd5e1", textTransform: "none", fontWeight: 400, marginLeft: 8, letterSpacing: 0 }}>
              · {filteredOrders.length} de {customerOrders.length}
            </span>
          )}
        </span>

        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          {isFiltered && (
            <button
              onClick={() => { setActiveFilter("all"); setSearchQuery(""); }}
              style={{
                background: "transparent", border: "1px solid #444",
                color: "#aaa", borderRadius: 5, padding: "5px 10px",
                fontSize: 11, cursor: "pointer",
              }}
              title="Limpiar filtros y búsqueda"
            >
              Limpiar filtros
            </button>
          )}
          <input
            type="search"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Buscar por N° OC, OT, cliente, SKU…"
            style={{
              background: "#161616", border: "1px solid #333", borderRadius: 5,
              padding: "6px 10px", fontSize: 12, color: "#ddd",
              fontFamily: "Arial,sans-serif", width: "min(280px, 100%)", boxSizing: "border-box",
            }}
          />
        </div>
      </div>

      <div style={{
        border: "1px solid #2e2e2e", borderRadius: 8,
        background: "#242424", overflowX: "auto",
      }}>
        <div style={{ minWidth: 1100 }}>
          <ExpandableTable
            columns={columns}
            rows={pageRows}
            renderDetail={renderDetail}
            onSave={handleSave}
            emptyText={
              loading
                ? "Cargando…"
                : isFiltered
                  ? "Ninguna OC coincide con los filtros"
                  : "Sin OC registradas"
            }
            initialExpandedId={pulidoLink.openId}
            initialEditingId={pulidoLink.editingId}
          />
        </div>
      </div>

      {/* paginación 50/pág igual a /trabajos */}
      {totalPages > 1 && (
        <div style={{
          display: "flex", justifyContent: "space-between",
          alignItems: "center", marginTop: 14, fontSize: 11, color: "#cbd5e1",
          flexWrap: "wrap", gap: 8, // móvil
        }}>
          <div>
            Mostrando {((page - 1) * PER_PAGE) + 1}–{Math.min(page * PER_PAGE, filteredOrders.length)} de {filteredOrders.length}
          </div>
          <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
            <button className="bini-btn-icon"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              style={{ opacity: page === 1 ? 0.4 : 1 }}>◂</button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
              .map((p, i, arr) => (
                <React.Fragment key={p}>
                  {i > 0 && arr[i - 1] !== p - 1 && (
                    <span style={{ color: "#999", padding: "0 4px" }}>…</span>
                  )}
                  <button className="bini-btn-icon"
                    onClick={() => setPage(p)}
                    style={{
                      background: p === page ? "#3498db" : "#2e2e2e",
                      color: p === page ? "#fff" : "#aaa",
                      minWidth: 28,
                    }}>{p}</button>
                </React.Fragment>
              ))}
            <button className="bini-btn-icon"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              style={{ opacity: page === totalPages ? 0.4 : 1 }}>▸</button>
          </div>
        </div>
      )}

      {/* modal eliminar */}
      <ConfirmModal
        open={!!deleteTarget}
        title={`Eliminar OC ${deleteTarget?.clientOCNumber || `N°${deleteTarget?.number}`}`}
        description={`Cliente: ${deleteTarget?.client?.name || "cliente no registrado"}`}
        warning={deleteTarget?.workOrders?.length > 0 ? {
          title: `Esta OC tiene OT vinculada`,
          description: "La OT quedará marcada como huérfana (origin: orphan) y se administra desde Trabajos.",
        } : null}
        confirmLabel="Eliminar OC"
        confirmVariant="danger"
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      {/* modal cancelar OC — cascade a WOs activas */}
      <ConfirmModal
        open={!!cancelTarget}
        title={`Cancelar OC ${cancelTarget?.clientOCNumber || `N°${cancelTarget?.number}`}`}
        description={`Cliente: ${cancelTarget?.client?.name || "cliente no registrado"} · ${(cancelTarget?.items?.length || 0)} ítem${(cancelTarget?.items?.length || 0) === 1 ? "" : "s"}`}
        warning={{
          title: "Cascade automática",
          description:
            "Las OTs activas asociadas a esta OC también se cancelarán. " +
            "OTs ya entregadas o facturadas se respetan (no se modifican). " +
            "La OC saldrá de /produccion (OCs activas e Items activos) y aparecerá en /workOrders → Canceladas.",
        }}
        confirmLabel={cancelling ? "Cancelando…" : "Sí, cancelar OC"}
        confirmVariant="warning"
        onConfirm={handleCancel}
        onCancel={() => !cancelling && setCancelTarget(null)}
      />

      {/* modal marcar facturada / editar facturación (componente compartido) */}
      <CierreFacturacionModal
        target={facturarTarget ? { co: facturarTarget, wo: facturarTarget.workOrders?.[0] } : null}
        onClose={() => setFacturarTarget(null)}
        onSaved={({ workOrder, customerOrder }) => {
          // Refrescar la fila — preferimos customerOrder; si solo vino workOrder
          // hacemos un refetch puntual para tener datos actualizados.
          if (customerOrder) {
            setCustomerOrders(p => p.map(c => c._id === customerOrder._id
              ? { ...c, ...customerOrder, items: normItems(customerOrder.items, customerOrder.workOrders?.[0]?.pieces || [c.workOrders?.[0]?.pieces].flat()) }
              : c
            ));
          } else if (workOrder && facturarTarget) {
            // Refetch la CO porque el endpoint WO devuelve solo la WO
            backend.get(`customerOrder/${facturarTarget._id}`).then(r => {
              setCustomerOrders(p => p.map(c => c._id === r.data._id
                ? { ...c, ...r.data, items: normItems(r.data.items, r.data.workOrders?.[0]?.pieces) }
                : c
              ));
            }).catch(() => {});
          }
          setFacturarTarget(null);
        }}
      />

      <Outlet />
    </div>
  );
};

// ─── Card top stats clickable (estándar 28-ago: tarjeta #232323 radio 12,
//     label 10/700 #8a8a8a, número tabular; el color semántico queda en el
//     texto y —cuando está activa— en el borde). Sigue filtrando al click. ───
const COCard = ({ label, value, color, onClick, active, title }) => (
  <div onClick={onClick} title={title || ""}
    className="t-02 tap-card"
    style={{
      flex: "1 1 0", minWidth: 130, padding: "12px 14px",
      background: active ? `${color}14` : "#232323",
      border: `1px solid ${active ? color : "#2e2e2e"}`,
      borderRadius: 12, cursor: onClick ? "pointer" : "default",
    }}
  >
    <div style={{
      fontSize: 10, fontWeight: 700, color: active ? color : "#8a8a8a",
      textTransform: "uppercase", letterSpacing: ".05em",
      display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6,
    }}>
      <span>{label}</span>
      {active && <span style={{ fontSize: 9, flexShrink: 0 }}>✕</span>}
    </div>
    <div style={{ fontSize: 22, fontWeight: 800, color, marginTop: 2, fontVariantNumeric: "tabular-nums" }}>{value}</div>
  </div>
);

export default CustomerOrders;
